import React, { useEffect, useState, Fragment, useRef } from 'react';
import { PageHeader } from '../../../../Common';
import services from '../services';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormHelperText from '@material-ui/core/FormHelperText';
import Joi from '@hapi/joi';
import { LoadingSpinerComponent } from '../../../../Common/loader';
import { trackPromise } from 'react-promise-tracker';
import authService from '../../../Common/services';
import tokenService from '../../../../Common/tokenDecoder';
import { BiometricDetailsReportSchemaObject } from '../BiometricDetailsReportSchema';
import { validateProperty, validate } from "../../../Common";
import ReactToPrint from "react-to-print";
import CreatePdf from './createPdf';
import xlsx from "json-as-xlsx";
import MessageModal from "../../../../UI/Modal/MessageModal";

const screenCode = 'BIOMETRICDETAILSREPORT';

export function isRegistered(value) {
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
}

export function getImageSrc(image) {
    if (!image) return null;
    if (/^(data:|https?:\/\/|\/)/i.test(image)) return image;
    return 'data:image/jpeg;base64,' + image; 
}

export function BiometricDetailsReportListing(props) {
    const componentRef = useRef();
    const [groups, setGroups] = useState();
    const [estates, setEstates] = useState();
    const [divisions, setDivisions] = useState();
    const [multipleEstates, setMultipleEstates] = useState([]);
    const [errors, setErrors] = useState({});
    const [reportData, setReportData] = useState([]);
    const [isTableHide, setIsTableHide] = useState(true);
    const [isReportButtonsEnabled, setIsReportButtonsEnabled] = useState(false);
    const [noDataWarning, setNoDataWarning] = useState(false);
    const [permissionList, setPermissions] = useState({
        isGroupFilterEnabled: false,
        isOperationEntityFilterEnabled: false
    });
    const [searchForm, setSearchForm] = useState({
        groupID: "0",
        estateID: "0",
        divisionID: "0"
    });
    const [selectedSearchValues, setSelectedSearchValues] = useState({
        group: '', estate: '', division: ''
    });
    const [messageModel, setMessageModel] = useState({
        hidden: true,
        type: "Success",
        disciption: "Completed Success",
    });

    const schema = Joi.object(BiometricDetailsReportSchemaObject).options({ allowUnknown: true });

    useEffect(() => { trackPromise(getPermissions()); }, []);
    useEffect(() => { trackPromise(getGroupsDropDown()); }, []);
    useEffect(() => { trackPromise(getEstatesForDropDown()); }, [searchForm.groupID]);
    useEffect(() => { trackPromise(getDivisionsForDropDown()); }, [searchForm.estateID]);

    async function getPermissions() {
        var permissions = await authService.getPermissionsByScreen(screenCode);
        var isAuthorized = permissions.find(p => p.permissionCode === 'VIEWBIOMETRICDETAILSREPORT');

        if (isAuthorized === undefined) {
            props.history.push('/unauthorized');
        }

        var isGroupFilterEnabled = permissions.find(p => p.permissionCode === 'GROUPDROPDOWN');
        var isOperationEntityFilterEnabled = permissions.find(p => p.permissionCode === 'OPERATIONENTITYDROPDOWN');

        setPermissions({
            ...permissionList,
            isGroupFilterEnabled: isGroupFilterEnabled !== undefined,
            isOperationEntityFilterEnabled: isOperationEntityFilterEnabled !== undefined
        });

        var token = tokenService.getUserDetailsFromTokenV2();
        if (token.operationEntities.length > 0) {
            var estateObject = [];
            for (let item of Object.entries(token.operationEntities)) {
                estateObject[item[1]["OperationEntityID"]] = item[1]["OperationEntityName"];
            }
            setMultipleEstates(estateObject);
            setSearchForm({
                ...searchForm,
                groupID: token.groupID.toString(),
                estateID: token.operationEntityID.toString()
            });
        } else {
            setSearchForm({
                ...searchForm,
                groupID: token.groupID.toString(),
                estateID: token.operationEntityID.toString()
            });
        }
    }

    async function getGroupsDropDown() {
        const groupData = await services.getGroupsForDropdown();
        setGroups(groupData);
    }

    async function getEstatesForDropDown() {
        if (searchForm.groupID === "0") { setEstates([]); return; }
        const estateData = await services.getEstatesByGroupID(searchForm.groupID);
        setEstates(estateData);
    }

    async function getDivisionsForDropDown() {
        if (searchForm.estateID === "0") { setDivisions([]); return; }
        const divisionData = await services.getDivisionByEstateID(searchForm.estateID);
        setDivisions(divisionData);
    }

    function generateDropDownMenu(data) {
        let items = [];
        if (data != null) {
            for (const [key, value] of Object.entries(data)) {
                items.push(<MenuItem key={key} value={key}>{value}</MenuItem>);
            }
        }
        return items;
    }

    function clearResults() {
        setReportData([]);
        setIsTableHide(true);
        setIsReportButtonsEnabled(false);
        setNoDataWarning(false);
    }

    function handleChange(e) {
        const target = e.target;
        const error = { ...errors };
        const errorMessage = validateProperty(target, BiometricDetailsReportSchemaObject);
        errorMessage ? error[target.name] = errorMessage : delete error[target.name];
        setErrors(error);

        if (target.name === 'groupID') {
            setSearchForm({ ...searchForm, groupID: target.value, estateID: "0", divisionID: "0" });
        } else if (target.name === 'estateID') {
            setSearchForm({ ...searchForm, estateID: target.value, divisionID: "0" });
        } else {
            setSearchForm({ ...searchForm, [target.name]: target.value });
        }
        clearResults();
    }


    async function handleSearch(e) {
        e.preventDefault();
        clearResults();

        const errorMessages = validate(schema, searchForm);
        errorMessages !== null ? setErrors(errorMessages) : setErrors({});
        if (errorMessages) return;

        const result = await trackPromise(
            services.getBiometricDetailsData(searchForm)
        );

        const rows = Array.isArray(result)
            ? result
            : (result?.data ?? []);

        setSelectedSearchValues({
            group: groups ? groups[searchForm.groupID] : '',
            estate: estates ? estates[searchForm.estateID] : '',
            division: divisions ? divisions[searchForm.divisionID] : ''
        });

        if (rows && rows.length > 0) {
            setReportData(rows);
            setIsTableHide(false);
            setIsReportButtonsEnabled(true);
            setNoDataWarning(false);
        } else {
            setReportData([]);
            setIsTableHide(true);
            setIsReportButtonsEnabled(false);

            setMessageModel({
                ...messageModel,
                hidden: false,
                type: "Error",
                disciption: "No Records To Display",
            });

            setTimeout(() => {
                modalDismissHandler();
            }, 3000);
        }
    }


    function createFile() {
        const settings = {
            sheetName: "Biometric Details Report",
            fileName: "BiometricDetailsReport",
            writeOptions: {},
        };

        const excelData = reportData.map((item) => ({
            employeeNo: item.employeeNo ?? "",
            employeeName: item.employeeName ?? "",
            division: item.division ?? "",
            registered: isRegistered(item.isRegistered)
                ? "Registered"
                : "Not Registered",
        }));

        excelData.push(
            {},
            {
                employeeNo: "Group: " + (selectedSearchValues.group || ""),
                employeeName: "Operation Entity: " + (selectedSearchValues.estate || ""),
                division: "Division: " + (selectedSearchValues.division || ""),
                registered: "Generated: " + new Date().toLocaleString(),
            },
            {
                employeeNo: "User: " + (tokenService.getUserNameFromToken() || ""),
            }
        );

        const excelHeaders = [
            { label: "Employee No", value: "employeeNo" },
            { label: "Name", value: "employeeName" },
            { label: "Division", value: "division" },
            { label: "Registered", value: "registered" },
        ];

        xlsx(excelHeaders, excelData, settings, true);
    }


    function onCancelClick() {
        setSearchForm({
            ...searchForm,
            divisionID: "0"
        });
        clearResults();
    }

    const estateOptions = multipleEstates.length > 0 || Object.keys(multipleEstates).length > 0
        ? multipleEstates : estates;


    function modalDismissHandler() {
        setMessageModel({ ...messageModel, hidden: true });
    }

    return (
        <Fragment>
            <LoadingSpinerComponent />
            <div className="row">
                <div className="card">
                    <div className="card-body" id="startPoint">
                        <div className="row">
                            <PageHeader title="Biometric Details Report" isButtonEnable={false} />
                        </div>
                        <form>
                            <div className="card-body">
                                <form>
                                    <div className="form-group row">
                                        <div className="col-md pb-1">
                                            <InputLabel shrink id="groupID-label">Group *</InputLabel>
                                            <Select
                                                fullWidth
                                                labelId="groupID-label"
                                                name="groupID"
                                                value={searchForm.groupID}
                                                onChange={handleChange}
                                                disabled={!permissionList.isGroupFilterEnabled}
                                            >
                                                <MenuItem value="0">Select Group</MenuItem>
                                                {generateDropDownMenu(groups)}
                                            </Select>
                                            {errors["groupID"] && <FormHelperText id="errbranchCode" className="text text-danger">{errors["groupID"]}</FormHelperText>}
                                        </div>

                                        <div className="col-md pb-1">
                                            <InputLabel shrink id="estateID-label">Operation Entity *</InputLabel>
                                            <Select
                                                fullWidth
                                                labelId="estateID-label"
                                                name="estateID"
                                                value={searchForm.estateID}
                                                onChange={handleChange}
                                                disabled={!permissionList.isOperationEntityFilterEnabled && multipleEstates.length === 0}
                                            >
                                                <MenuItem value="0">Select Operation Entity</MenuItem>
                                                {generateDropDownMenu(estateOptions)}
                                            </Select>
                                            {errors["estateID"] && <FormHelperText id="errbranchCode" className="text text-danger">{errors["estateID"]}</FormHelperText>}
                                        </div>

                                        <div className="col-md pb-1">
                                            <InputLabel shrink id="divisionID-label">Division *</InputLabel>
                                            <Select
                                                fullWidth
                                                labelId="divisionID-label"
                                                name="divisionID"
                                                value={searchForm.divisionID}
                                                onChange={handleChange}
                                            >
                                                <MenuItem value="0">Select Division</MenuItem>
                                                {generateDropDownMenu(divisions)}
                                            </Select>
                                            {errors["divisionID"] && <FormHelperText id="errbranchCode" className="text text-danger">{errors["divisionID"]}</FormHelperText>}
                                        </div>
                                    </div>
                                    <div className="form-group row">
                                        <div className="col-md pb-1">
                                            <input
                                                type="submit"
                                                className="btn btn-primary"
                                                onClick={e => trackPromise(handleSearch(e))}
                                                value="Search"
                                            />
                                            <input type="reset"
                                                className="btn btn-warning pull-right"
                                                value="Clear"
                                                onClick={onCancelClick}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>

                            {noDataWarning &&
                                <div className="alert alert-warning mt-3">No records found for the selected filters.</div>
                            }
                        </form>
                        {!isTableHide &&
                            <div className="card mt-3">
                                <div className="card-body" style={{ overflowX: 'auto' }}>
                                    <table id="biometric-details-table" className="display dataTable no-footer" style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th>Employee No</th>
                                                <th>Name</th>
                                                <th>Division</th>
                                                <th style={{ textAlign: 'center' }}>Registered</th>
                                                <th style={{ textAlign: 'center' }}>Image</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.map((item, index) => {
                                                const src = getImageSrc(item.image);
                                                const registered = isRegistered(item.isRegistered);
                                                return (
                                                    <tr key={index}>
                                                        <td>{item.employeeNo}</td>
                                                        <td>{item.employeeName}</td>
                                                        <td>{item.division}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span className={registered ? 'badge badge-success' : 'badge badge-danger'}>
                                                                {registered ? 'Registered' : 'Not Registered'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {src
                                                                ? <img src={src} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />
                                                                : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    <table id="biometric-details-excel-table" hidden={true}>
                                        <thead>
                                            <tr>
                                                <th>Employee No</th>
                                                <th>Name</th>
                                                <th>Division</th>
                                                <th>Registered</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.employeeNo}</td>
                                                    <td>{item.employeeName}</td>
                                                    <td>{item.division}</td>
                                                    <td>{isRegistered(item.isRegistered) ? 'Registered' : 'Not Registered'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        }
                    </div>
                    {reportData.length > 0 ?
                        <div className="pt-2 mr-2">
                            <ReactToPrint
                                documentTitle={"Biometric Details Report"}
                                trigger={() => <input type="button" className="btn btn-primary pull-right ml-2" value="PDF" />}
                                content={() => componentRef.current}
                            />
                            <div hidden={true}>
                                <CreatePdf ref={componentRef} data={reportData} searchData={selectedSearchValues} />
                            </div>
                            <input
                                type="button"
                                className="btn btn-primary pull-right ml-2"
                                value="Excel"
                                onClick={createFile}
                            />
                        </div>
                        : null
                    }
                </div >
            </div >
            <MessageModal
                hidden={messageModel.hidden}
                messageType={messageModel.type}
                messageDescription={messageModel.disciption}
            />
        </Fragment >
    );
}